import { Component, inject, OnInit, signal, SecurityContext } from '@angular/core';
import { Model, SurveyModel } from 'survey-core';
import { PlainLightPanelless } from 'survey-core/themes'
import { SurveyModule } from 'survey-angular-ui';
import { EpreopApiService } from '../../services/epreop-api.service';
import { ActivatedRoute } from '@angular/router';
import { SurveyItem } from '../../models/patient-questionnaire';
import { baseSurveyJson, surveyCss } from '../../models/base-survey';
import markdownit from 'markdown-it';
import { SurveyResponse } from '../../models/survey-response';
import { Answer } from '../../models/survey-answer';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-survey',
  imports: [SurveyModule],
  templateUrl: './survey.component.html',
  styleUrl: './survey.component.css'
})
export class SurveyComponent implements OnInit {
  private ePreopApi: EpreopApiService = inject(EpreopApiService);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private sanitizer: DomSanitizer = inject(DomSanitizer); 

  private boundPartialSaveHandler = this.sendDataToServer.bind(this);
  private boundCompleteSaveHandler = this.sendCompletedDataToServer.bind(this);
  readonly storageItemKey = "patEx_";

  surveyModel: Model | null;
  id: string;
  surveyResponse: SurveyResponse = {
    patientQuestionnaireId: "",
    patientId: "",
    practiceId: "",
    questionnaireId: "",
    answers: [],
    isComplete: false
  };

  surveyCompleted = signal(false);
  errorMsg = signal("");

  ngOnInit() {
    setTimeout(() => {
      if (!this.surveyModel) {
        this.errorMsg.set("Error 598.34");
      }
    }, 5000);

    this.id = this.route.snapshot.paramMap.get('id');
    this.ePreopApi
      .getSurveyById(this.id)
      .then(res => {
        // Sanitized logging - only log success/failure status instead of full response data
        console.log(`Survey API call: ${res.success ? 'success' : 'failed'}`);
        if (res.success && res.items) {
          this.mapSurveyModel(res.items);
          // Removed potentially unsafe logging of survey model data
        } else {
          console.log("Unsuccessful Code");
          const errorCode = res.messages.length > 0 ? "553.10" : "Not Valid Survey For User";
          this.errorMsg.set(errorCode);
          // Removed potentially unsafe logging of error message
        }
      })
      .catch(err => {
        // Sanitized error logging - avoid logging untrusted error message
        console.log("Survey API call failed", {status: err?.status, statusCode: err?.statusCode, timestamp: new Date().toISOString()});
      });
  }

  mapSurveyModel(surveyItems: SurveyItem[]): void {
    if (surveyItems.length == 0) {
      return null;
    }

    const surveyItem = surveyItems[0]; // Should only have one survey
    this.surveyResponse.patientQuestionnaireId = this.id;
    this.surveyResponse.patientId = surveyItem.patientId;
    this.surveyResponse.practiceId = surveyItem.practiceId;
    this.surveyResponse.questionnaireId = surveyItem.questionnaireId;

    if (surveyItem.status == "Completed") {
      this.surveyCompleted.set(true);
      return null;
    } else {
      this.surveyModel = new Model(baseSurveyJson);
      const answers = Object.values(surveyItems[0].answers);

      if (surveyItem.questions) {
        let pageCounter = 1;
        for (const question of Object.values(surveyItem.questions)) {
          const pageModel = this.surveyModel.createNewPage("page" + pageCounter);

          switch (question.type) {
            case 'NumberChoice':
            case 'TextChoice': {
              const choices = [];
              for (const questionAnswer of question.answers) {
                const answer = answers.find(x => x.id === questionAnswer.id);
                if (answer) {
                  choices.push({ 
                    value: answer.id + "^" + answer.value, 
                    text: this.sanitizeText(answer.text) // Sanitize choice text
                  });
                }
              }

              if (choices.length > 0) {
                const newQuestion = pageModel.addNewQuestion("radiogroup", question.id);
                newQuestion.title = this.replacePlaceholder(question.text!);
                newQuestion['choices'] = choices;
              } else {
                continue;
              }
              break;
            }
            case 'Number':
            case 'Date':
            case 'Phone': {
              let maxLength = 20;
              const newQuestion = pageModel.addNewQuestion("text", question.id);
              newQuestion.title = this.sanitizeText(question.text!); // Sanitize title

              if (question.type == 'Number') {
                maxLength = 10;
              }
              newQuestion['maxLength'] = maxLength;
              break;
            }
            case 'Text': {
              const newTextQuestion = pageModel.addNewQuestion("comment", question.id);
              newTextQuestion.title = this.sanitizeText(question.text!); // Sanitize title
              newTextQuestion['maxLength'] = 100;
              break;
            }
            default:
              continue;
          }

          this.surveyModel.addPage(pageModel);
          pageCounter++;
        }
      }

      this.addFrontPageHeader();
      this.addSurveyEventHandlers();

      const prevData = window.localStorage.getItem(this.storageItemKey + this.id) || null;
      if (prevData) {
        const data = JSON.parse(prevData);
        this.surveyModel.data = data;
        if (data.pageNo) {
          this.surveyModel.currentPageNo = data.pageNo;
        }
      }
    }
  }

  replacePlaceholder(stringValue: string): string {
    const escaped = stringValue.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#x27;');

    return escaped.replace(/\[BREAK\]/g, '<br/>').replace(/\[BOLDSTART\]/g, '<b>').replace(/\[BOLDEND\]/g, '</b>');

  }

  addFrontPageHeader(): void {
    const frontPage = this.surveyModel.PageCount > 0
      ? this.surveyModel.getPage(0)
      : this.surveyModel.addNewPage("page1");
    frontPage.navigationButtonsVisibility = "hide";
    const frontPageElement = frontPage.addNewQuestion("html", "surveyFrontPage", 0);
    frontPageElement['html'] = "<div class=\"container\">" +
      "<div class=\"row\">" +
      "<div class=\"col-12\">" +
      "<h5 class=\"card-title\">Welcome to ePreop's Patient Experience survey.</h5>" +
      "</div>" +
      "</div>" +
      "<div class=\"row\">" +
      "<div class=\"col-12\">" +
      "<p class=\"card-text\">" +
      "Your Healthcare Provider would like you to take the following brief survey; it will only take a few minutes. " +
      "By participating, you will help ensure patients can receive the highest possible quality of care." +
      "</p>" +
      "</div>" +
      "</div>" +
      "</div><hr />\n";
  }

  addSurveyEventHandlers(): void {
    // Renders HTML in Question's Text - SANITIZED
    this.surveyModel.onTextMarkdown.add((_, options) => {
      // Disable HTML parsing in markdown to prevent XSS
      const str = markdownit({ html: true }).renderInline(options.text);
      // If you need to allow specific HTML, sanitize it:
      options.html = this.sanitizer.sanitize(SecurityContext.HTML, str);
      //options.html = str;
    });

    this.surveyModel.onPartialSend.add(this.boundPartialSaveHandler);
    this.surveyModel.onComplete.add(this.boundCompleteSaveHandler);
    this.surveyModel.applyTheme(PlainLightPanelless);
    this.surveyModel.css = surveyCss;
    this.surveyModel.onAfterRenderQuestion.add((_, options) => {
      options.htmlElement.querySelectorAll("input[type='radio'").forEach(el => {
        el.parentElement.insertAdjacentHTML("afterbegin", "<div class=\"button-div\"><span class=\"btn-label\"></span></div>");
      });
    });
  }

  /// ---------------------------------------------------------------
  /// sendDataToServer
  /// sends the response back to the server after each page
  /// is completed by the patient
  /// ---------------------------------------------------------------
  sendDataToServer(survey: SurveyModel): void {
    const data = survey.data;
    data.pageNo = survey.currentPageNo;
    window.localStorage.setItem(this.storageItemKey + this.id, JSON.stringify(data));

    this.setResponseAnswers(survey.data, false);
    this.ePreopApi.sendSurveyData(this.id, this.surveyResponse).then(() => { this.surveyResponse.answers = [] });
  }

  /// ---------------------------------------------------------------
  /// sendCompletedDataToServer
  /// sends the response back to the server once the survey
  /// has been completed
  /// ---------------------------------------------------------------
  sendCompletedDataToServer(survey: SurveyModel): void {
    window.localStorage.setItem(this.storageItemKey + this.id, "");
    this.setResponseAnswers(survey.data, true);
    this.ePreopApi.sendSurveyData(this.id, this.surveyResponse).then(() => { this.surveyResponse.answers = [] });
  }

  setResponseAnswers(surveyAnswers, isComplete: boolean): void {
    this.surveyResponse.isComplete = isComplete;
    // Removed logging of survey answers to prevent log forging with user data
    let answerValue = "";
    let answerId = "";
    let answer: Answer;

    for (const key in surveyAnswers) {
      if (key == 'pageNo') {
        continue;
      }
      if (surveyAnswers[key]) {
        const aArray = surveyAnswers[key].split('^');

        answerValue = "";
        answerId = "";

        //if the array is only length 1 then it is a free form answer
        //set the value and not the answer id
        if (aArray.length == 1) {
          answerValue = aArray[0];
          answerId = null;
        }

        //if the array is length 2 then there is an answer id and value
        //set both
        if (aArray.length > 1) {
          answerValue = aArray[1];
          answerId = aArray[0];
        }
        answer = {
          answerId: answerId,
          questionId: key,
          value: answerValue
        };

        this.surveyResponse.answers.push(answer);
      }
    }
  }

  /**
   * Sanitizes text input to prevent XSS attacks
   * Escapes HTML entities while preserving plain text
   */
  private sanitizeText(text: string): string {
    if (!text) return '';
    
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#x27;')
      .replace(/\//g, '&#x2F;');
  }
}
